import React, { useState, useRef, Fragment } from 'react'
import { useTraversal, FileUpload } from '@guillotinaweb/react-gmi'
import {
  Button,
  Table,
  InputNumber,
  Switch,
  message,
  Affix,
  Select,
  Radio,
  Space,
} from 'antd'
import { MenuOutlined } from '@ant-design/icons'
import {
  sortableContainer,
  sortableElement,
  sortableHandle,
} from 'react-sortable-hoc'
import useSaveProduct from './useSaveProduct'
import { I18NField, I18NRichText } from '../../../components/i18nfield'
import { ProductSelector } from '../../../components/product-selector'
import { SelectTaxonomy } from '../../../components/select-taxonomy'
import { arrayMove, getCapacityText } from '../../../utils'
import { getCdnUrl, BOTTLE_CAPACITY_LITERS } from '../../../consts'
import { ProductName } from '../../../components/product-name'

import './styles.css'

const cdn = getCdnUrl()
const fallbackImg = `${cdn}frontend/pack_fallback.svg`

const DragHandle = sortableHandle(() => (
  <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
))

const SortableItem = sortableElement((props) => <tr {...props} />)
const SortableContainer = sortableContainer((props) => <tbody {...props} />)

export function PackEditor() {
  const ctx = useTraversal()
  const [generating, setGenerating] = useState(false)
  const [saving, save] = useSaveProduct()
  const [uploading, setUploading] = useState(false)
  const cache = useRef({})
  const pack = ctx.state.context
  const imgPath = 'api.behaviors.images.IImagesBehavior'
  const [img, setImg] = useState(pack[imgPath]?.images?.anv800?.path)
  const [names, setNames] = useState(pack.nombres)
  const [labels, setLabels] = useState(pack.labels || [])
  const [description, setDescription] = useState(pack.localized_text || {})
  const [discount, setDiscount] = useState((pack.descuento_del_pack || 0) * 100)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [products, setProducts] = useState(() =>
    Object.keys(pack.contenido_del_pack)
      .sort(
        (a, b) =>
          pack.contenido_del_pack[a].orden - pack.contenido_del_pack[b].orden
      )
      .map((ref, index) => ({
        ...pack.contenido_del_pack[ref],
        ref,
        index,
      }))
  )

  const somePresent = products.some((p) => p.regalo)

  const referenciaPack = `es_${pack.referencia_pack}`
  const solo_venta_privada =
    pack.opciones[referenciaPack].otros.solo_venta_privada
  const [optionVentaPrivada, setOptionVentaPrivada] = useState(
    solo_venta_privada
  )
  const onChangeVentaPrivada = (e) => {
    setOptionVentaPrivada(e.target.value)
  }

  function updateProductState(product) {
    setProducts((ps) =>
      ps.map((p) => (p.index === product.index ? product : p))
    )
  }

  function cleanProduct(p) {
    return {
      cantidad: p.cantidad || 1,
      orden: p.index,
      regalo: p.regalo || false,
    }
  }

  async function onSave(transparent = false) {
    if (products.some((p) => typeof p.ref === 'undefined')) {
      return message.error(
        'You seem to have left out filling in product information! Fill in all fields and save again.'
      )
    }
    await save(
      {
        nombre: names.es.nombre || pack.nombre,
        localized_text: description,
        labels,
        descuento_del_pack: discount / 100,
        nombres: names,
        contenido_del_pack: products.reduce((ob, c) => {
          ob[c.ref] = cleanProduct(c)
          return ob
        }, {}),
      },
      transparent
    )
  }

  async function descataloguePack() {
    var optionPack = null
    var messageSuccess = ''
    if (pack.pack_descatalogado) {
      optionPack = false
      messageSuccess = 'Pack available'
    } else {
      optionPack = true
      messageSuccess = 'Pack descatalogued'
    }
    try {
      const res = await ctx.client.rest.request(ctx.path, {
        method: 'PATCH',
        body: JSON.stringify({ pack_descatalogado: optionPack }),
      })
      if (res.status === 204) {
        message.success(messageSuccess)
        ctx.refresh()
      } else {
        message.error('Error when changing pack status')
      }
    } catch (e) {
      message.error(`Oops! ${e.message}`)
    }
  }

  async function ventaPrivada() {
    var messageSuccess = 'The private sale status has been changed'
    const endpoint = `${ctx.path}@opciones`
    try {
      const res = await ctx.client.rest.request(endpoint, {
        method: 'PATCH',
        body: JSON.stringify({
          opciones_to_modify: {
            solo_venta_privada: optionVentaPrivada,
          },
        }),
      })
      if (res.status === 204) {
        message.success(messageSuccess)
        ctx.refresh()
      } else {
        message.error('Error when changing pack status')
      }
    } catch (e) {
      message.error(`Oops! ${e.message}`)
    }
  }

  async function generateImage() {
    setGenerating(true)
    try {
      await onSave(true)
      const res = await ctx.client.rest.request(
        `${ctx.path}@generate-image`,
        { method: 'POST' },
        headersForImageEndpoinds(ctx)
      )
      const { path } = await res.json()
      setImg(path)
      message.success('Image generated and saved')
      ctx.refresh()
    } catch (e) {
      message.error(`Oops! ${e.message}`)
    }
    setGenerating(false)
  }

  async function uploadImage(file) {
    setUploading(true)

    try {
      const res = await ctx.client.rest.request(
        `${ctx.path}@upload-image/${pack.referencia_pack}/anv800`,
        {
          method: 'POST',
          body: file.data,
        },
        headersForImageEndpoinds(ctx, file)
      )
      if (res.status >= 300)
        throw new Error('There was an error uploading the image')
      const { path } = await res.json()
      setImg(path)
      message.success('Image uploaded and saved')
      ctx.refresh()
    } catch (e) {
      message.error(`Oops! ${e.message}`)
    }
    setUploading(false)
  }

  return (
    <div>
      <Affix>
        <div
          style={{
            backgroundColor: 'white',
            display: 'flex',
            padding: 10,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h1 className="title is-size-4 is-primary">{pack.nombre}</h1>
          <Button type="primary" disabled={saving} onClick={() => onSave()}>
            {saving ? 'Saving...' : 'Save pack'}
          </Button>
        </div>
      </Affix>
      {pack['tipo'] === '/pk' && (
        <div>
          <p>
            {pack.pack_descatalogado
              ? 'Make the pack available'
              : 'Descatalogue the pack when the products in the pack are in a selling condition.'}
          </p>
          <Button onClick={descataloguePack}>
            {pack.pack_descatalogado
              ? 'Make pack available'
              : 'Descatalogue Pack'}
          </Button>
        </div>
      )}
      <div style={{ padding: 20 }}>
        <h2 className="title is-size-6">Pack name</h2>
        <I18NField Widget={() => null}>
          {(lang) => (
            <Fragment key={`name_${lang}`}>
              <ProductName
                label="full name"
                names={names}
                lang={lang}
                field="nombre"
                setNames={setNames}
              />
              <ProductName
                label="short name"
                names={names}
                lang={lang}
                field="nombre_corto"
                setNames={setNames}
              />
              <ProductName
                label="banner name"
                names={names}
                lang={lang}
                field="nombre_banner"
                setNames={setNames}
              />
            </Fragment>
          )}
        </I18NField>
        <hr />
        <h2 className="title is-size-6">Description</h2>
        <I18NRichText
          vals={description}
          onChange={setDescription}
          modifyContent
        />
        <hr />
        <h2 className="title is-size-6">Labels</h2>
        <SelectTaxonomy
          taxonomy="clasificaciones_productos"
          value={labels}
          onChange={setLabels}
          placeholder="Select pack labels"
        />
        <hr />
        <h2 className="title is-size-6">Discount in % (optional)</h2>
        <InputNumber
          disabled={somePresent}
          max={100}
          min={0}
          onChange={(v) => setDiscount(v)}
          placeholder="Write the discount in %"
          value={discount}
        />
        {somePresent && (
          <div style={{ fontSize: 12, padding: 10, fontStyle: 'italic' }}>
            Disable the products that are gifts in order to add a discount to
            the pack.
          </div>
        )}
        <hr />
        <h2 className="title is-size-6">Pack Products</h2>
        <Table
          dataSource={products}
          pagination={false}
          columns={[
            {
              title: 'Sort',
              dataIndex: 'sort',
              width: 30,
              className: 'drag-visible',
              render: () => <DragHandle />,
            },
            {
              title: 'Quantity',
              dataIndex: 'cantidad',
              width: 100,
              className: 'drag-visible',
              key: 'cantidad',
              render: (v, o) => (
                <InputNumber
                  value={v}
                  onChange={(cantidad) =>
                    updateProductState({ ...o, cantidad })
                  }
                  min={1}
                />
              ),
            },
            {
              title: 'Product',
              key: 'ref',
              className: 'drag-visible',
              width: 500,
              dataIndex: 'ref',
              render: (v, o) => (
                <ProductColumn
                  cache={cache}
                  value={v}
                  column={o}
                  products={products}
                  updateProductState={updateProductState}
                />
              ),
            },
            {
              title: 'Present',
              dataIndex: 'regalo',
              width: 30,
              key: 'regalo',
              render: (v, o) => (
                <Switch
                  checked={v}
                  onChange={(regalo) => {
                    setDiscount(0)
                    updateProductState({ ...o, regalo })
                  }}
                />
              ),
            },
          ]}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          components={{
            body: {
              wrapper: (props) => (
                <SortableContainer
                  useDragHandle
                  disableAutoscroll
                  helperClass="row-dragging"
                  onSortEnd={({ oldIndex, newIndex }) => {
                    if (oldIndex !== newIndex) {
                      const newProds = arrayMove(
                        products,
                        oldIndex,
                        newIndex
                      ).map((p, index) => ({ ...p, index }))

                      setProducts(newProds)
                    }
                  }}
                  {...props}
                />
              ),
              row: ({ className, style, ...restProps }) => {
                const index = products.findIndex(
                  (x) => x.index === restProps['data-row-key']
                )
                return <SortableItem index={index} {...restProps} />
              },
            },
          }}
          rowKey={(record) => record.index}
          size="small"
        />
        <div
          style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}
        >
          {selectedRowKeys.length > 0 && (
            <Button
              style={{ marginRight: 10 }}
              onClick={() => {
                setProducts((ps) =>
                  ps.filter((p) => !selectedRowKeys.includes(p.index))
                )
                setSelectedRowKeys([])
              }}
            >
              Remove selected
            </Button>
          )}
          <Button
            onClick={() =>
              setProducts((p) => [
                ...p,
                { cantidad: 1, regalo: false, index: p.length },
              ])
            }
          >
            Add new product
          </Button>
        </div>
        <hr />
        <h2 className="title is-size-6">Pack Image</h2>
        <div className="pack-image-btns">
          <Button
            disabled={generating}
            onClick={generateImage}
            style={{ marginRight: 10 }}
          >
            {generating ? 'Generating...' : 'Generate image'}
          </Button>
          <FileUpload
            accept="image/png"
            onChange={uploadImage}
            label={uploading ? 'Uploading...' : 'Upload an image'}
          />
        </div>
        <div style={{ textAlign: 'center' }}>
          <img
            style={{ margin: '10px auto', display: 'block' }}
            width={453}
            height={290}
            onError={(e) => {
              e.currentTarget.src = fallbackImg
              e.currentTarget.style.display = 'block'
            }}
            src={
              img
                ? `${cdn}${img}`
                : `${cdn}prfmtgrande/pk/${pack.referencia_pack}_anv800.png`
            }
          />
        </div>
        <h2 className="title is-size-6">Venta privada</h2>
        <Space direction="vertical">
          <Radio.Group
            onChange={onChangeVentaPrivada}
            value={optionVentaPrivada}
          >
            <Radio value={true}>Venta privada</Radio>
            <Radio value={false}>Venta publica</Radio>
          </Radio.Group>
          <Button onClick={ventaPrivada}>Update State</Button>
        </Space>
      </div>
    </div>
  )
}

function ProductColumn({ products, updateProductState, cache, value, column }) {
  const type = value?.includes('#') ? 'group' : 'refs'
  const [capacity, setCapacity] = useState(value?.split('#')?.[1] || '75')
  const filteredProducts = (value ? [value.split('#')[0]] : []).filter(
    (v) => v && v !== '#'
  )

  function onChangeType(e) {
    updateProductState({
      ...column,
      ref: e === 'group' ? '#' : '',
      nombre: undefined,
    })
  }

  return (
    <div style={{ display: 'flex' }} key={type}>
      <Select defaultValue={type} onChange={onChangeType}>
        <Select.Option key="refs">Refs</Select.Option>
        <Select.Option key="group">Group</Select.Option>
      </Select>
      <ProductSelector
        placeholder=" "
        key={type}
        onChangeProductInfo={(pnames) => {
          cache.current[value] = pnames
        }}
        productTypes={['!/pk', '!/co/pk']}
        defaultProductInfo={cache.current[value]}
        disabledIds={new Set(products.filter((p) => !p.add).map((p) => p.ref))}
        products={filteredProducts}
        type={type}
        onChange={(refs, prods) => {
          let ref = refs[refs.length - 1]

          if (type === 'group') ref += `#${capacity}`

          updateProductState({
            ...column,
            ref,
            nombre: prods.find((p) => p.ref === ref)?.nombre,
          })
        }}
      />
      {type === 'group' && (
        <Select
          key="capacity"
          defaultValue={capacity}
          onChange={(newCapacity) => {
            setCapacity(newCapacity)
            updateProductState({
              ...column,
              ref: column.ref.replace(`#${capacity}`, `#${newCapacity}`),
            })
          }}
        >
          {Object.keys(BOTTLE_CAPACITY_LITERS).map((c) => (
            <Select.Option key={c}>{getCapacityText(c)}</Select.Option>
          ))}
        </Select>
      )}
    </div>
  )
}

function headersForImageEndpoinds(ctx, file) {
  const key = `last-mod-date:${ctx.path}`
  const headers = ctx.client.rest.getHeaders()

  headers['last-mod-date'] = sessionStorage.getItem(key)

  if (file) {
    headers['Content-Type'] = file['content-type']
  }

  // As just after the requests we do a ctx.refresh,
  // we clean up the last-mod-date to take the value
  // from the refresh response.
  sessionStorage.removeItem(key)

  return headers
}
